import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapCanvas from '../map/MapCanvas.jsx';
import { api } from '../lib/api.js';

/**
 * The map that sits under a place field.
 *
 * Every place in the app is chosen the same way, so every one of them gets the
 * same map: the outing's destination and meeting point, a ride's departure, and
 * each stop along the way. Consistency here is not tidiness, it is the whole
 * reason the control is learnable.
 *
 * It is a thumbnail until it is touched. A thumbnail is enough to confirm a pin
 * and useless for navigating, so the moment someone pans, zooms or starts
 * placing a point it grows, and it shrinks again as soon as attention moves
 * back to the form. Only one of them is ever large at a time.
 */

/** Small broadcast so opening one map folds the others, with no parent plumbing. */
const EXPANDED = 'covoiturage:place-map-expanded';

/**
 * Search that moves the map rather than choosing for you.
 *
 * The field above already resolves a name straight to a place, which is right
 * when the name is the answer. It is wrong for a trailhead car park, a lay-by or
 * a spot in a wood: those have no address worth searching, but they are two
 * hundred metres from something that does. So this takes you to the area and
 * stops there. The point is still placed by clicking, deliberately.
 *
 * It rides on the same free, cached Nominatim proxy as everything else, so it
 * adds no service and no key.
 */
function MapSearch({ onGoTo, open, setOpen }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cursor, setCursor] = useState(-1);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 3) {
      setResults([]);
      setBusy(false);
      return undefined;
    }
    const controller = new AbortController();
    setBusy(true);
    const timer = setTimeout(() => {
      api
        .searchPlaces(term, controller.signal)
        .then(({ results: found }) => {
          setResults(found);
          setOpen(true);
          setCursor(-1);
        })
        .catch(() => {})
        .finally(() => setBusy(false));
    }, 420);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  return (
    <div className="map-search">
      <div className="map-search-bar">
        <span aria-hidden="true">🔍</span>
        <input
          className="input"
          value={query}
          placeholder="Aller a un lieu, une ville..."
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          /**
           * This field lives inside the outing form, where Enter would otherwise
           * submit it. Enter here means "go to this result", so the key is always
           * swallowed, including while the search is still running: submitting an
           * outing because someone confirmed a search is never what was meant.
           */
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const pick = results[cursor >= 0 ? cursor : 0];
              if (pick) {
                onGoTo(pick);
                setOpen(false);
              }
              return;
            }
            if (!open || results.length === 0) return;
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setCursor((c) => (c + 1) % results.length);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setCursor((c) => (c - 1 + results.length) % results.length);
            }
          }}
          aria-label="Deplacer la carte vers un lieu"
        />
        {busy && <span className="spinner" />}
        {query && !busy && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setQuery('');
              setResults([]);
              setOpen(false);
            }}
            aria-label="Effacer"
          >
            ✕
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div className="map-search-results">
          {results.map((place, i) => (
            <button
              type="button"
              key={`${place.lat},${place.lng},${i}`}
              className={`place-result ${i === cursor ? 'active' : ''}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => {
                onGoTo(place);
                setOpen(false);
              }}
            >
              <div className="place-result-title truncate">{place.label}</div>
              <div className="place-result-sub truncate">{place.address}</div>
            </button>
          ))}
          <p className="small faint" style={{ padding: '6px 10px 4px' }}>
            La carte s'y deplace. Touchez ensuite l'endroit exact.
          </p>
        </div>
      )}
      {open && !busy && query.trim().length >= 3 && results.length === 0 && (
        <div className="map-search-results">
          <p className="small faint" style={{ padding: '8px 10px' }}>Aucun resultat.</p>
        </div>
      )}
    </div>
  );
}

export default function PlaceMap({
  value,
  onChange,
  picking,
  onPickingChange,
  contextMarkers = [],
  tiles,
  accent = '#ff7a45',
}) {
  const [expanded, setExpanded] = useState(false);
  const [focusOn, setFocusOn] = useState(null);
  /** Where a search landed. Shown, snappable, never chosen on your behalf. */
  const [searched, setSearched] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const boxRef = useRef(null);
  const id = useRef(Math.random().toString(36).slice(2)).current;

  const expand = useCallback(() => {
    setExpanded(true);
    window.dispatchEvent(new CustomEvent(EXPANDED, { detail: id }));
    /**
     * Bring the grown map into view, once it has finished growing. Scrolling on
     * the next frame scrolls for the old height, which on a short screen leaves
     * the bottom of the map below the fold: precisely the part someone is about
     * to tap.
     */
    const box = boxRef.current;
    if (!box) return;
    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      box.removeEventListener('transitionend', reveal);
      box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    };
    box.addEventListener('transitionend', reveal);
    setTimeout(reveal, 360); // in case the transition never fires
  }, [id]);

  useEffect(() => {
    if (picking) expand();
  }, [picking, expand]);

  // Another map opened, or the person went back to typing: stand down.
  const pickingRef = useRef(picking);
  pickingRef.current = picking;

  useEffect(() => {
    const onOther = (e) => {
      if (e.detail !== id && !pickingRef.current) setExpanded(false);
    };
    const onFocus = (e) => {
      // Never fold while a point is being placed, and ignore focus simply
      // falling to the body: a control unmounting is not someone leaving.
      if (pickingRef.current || e.target === document.body) return;
      if (!boxRef.current?.parentElement?.contains(e.target)) setExpanded(false);
    };
    window.addEventListener(EXPANDED, onOther);
    document.addEventListener('focusin', onFocus);
    return () => {
      window.removeEventListener(EXPANDED, onOther);
      document.removeEventListener('focusin', onFocus);
    };
  }, [id]);

  const markers = useMemo(() => {
    const out = contextMarkers
      .filter((m) => m?.lat != null)
      .map((m) => ({ ...m, dimmed: true, signature: `ctx:${m.id}:${m.lat}:${m.lng}` }));
    if (searched?.lat != null && (!value || searched.lat !== value.lat || searched.lng !== value.lng)) {
      out.push({
        id: 'searched',
        kind: 'meet',
        glyph: '🔍',
        lat: searched.lat,
        lng: searched.lng,
        label: searched.label,
        place: searched,
        signature: `found:${searched.lat}:${searched.lng}`,
      });
    }
    if (value?.lat != null) {
      out.push({
        id: 'chosen',
        kind: 'origin',
        color: accent,
        glyph: '✓',
        lat: value.lat,
        lng: value.lng,
        label: value.label,
        place: value,
        signature: `chosen:${value.lat}:${value.lng}`,
      });
    }
    return out;
  }, [contextMarkers, value, accent, searched]);

  /**
   * While placing, the point being moved must not drive the framing: re-fitting
   * on every click would slide the map out from under the finger that is aiming.
   */
  const framed = markers.filter((m) => m.id !== 'searched' && !(picking && m.id === 'chosen'));
  const fitBounds = framed.length ? framed.map((m) => [m.lat, m.lng]) : null;
  const fitKey = `${framed.map((m) => m.signature).join('|')}:${expanded ? 'big' : 'small'}`;

  /**
   * Placing a point does not end at the first click.
   *
   * The first attempt is rarely the right spot: a car park entrance, the far end
   * of a lay-by, the other side of a roundabout. Ending the mode on the first
   * click meant reaching for a button to try again, every time. So each click
   * moves the point and the mode stays open until it is closed deliberately.
   */
  const clickSeq = useRef(0);

  const handleClick = useCallback(
    async ({ lat, lng, place: snapped }) => {
      if (!picking) return;
      // Reverse geocoding is slow enough that a second click can land first. Each
      // click takes a ticket, and a late answer for an older one is dropped:
      // without this, an earlier address would be written back over the newer
      // position, quietly moving the point the person just placed.
      const seq = clickSeq.current + 1;
      clickSeq.current = seq;

      setSearched(null);

      if (snapped?.label) {
        onChange({ label: snapped.label, address: snapped.address ?? '', lat, lng });
        return;
      }

      onChange({ label: `${lat.toFixed(4)}, ${lng.toFixed(4)}`, address: '', lat, lng });
      try {
        const { place } = await api.reverse(lat, lng);
        if (seq !== clickSeq.current) return; // a newer click already won
        onChange({ ...place, lat, lng });
      } catch {
        /* the coordinates already stand on their own */
      }
    },
    [picking, onChange],
  );

  /**
   * Escape peels one layer at a time: the search list, then placing, and only
   * then whatever encloses them.
   *
   * It is decided here, in one handler that knows both states, rather than in
   * each of them. Splitting it made the outcome depend on which listener
   * happened to register first, and the enclosing dialog won that race: pressing
   * Escape to dismiss a list of search results closed the entire form.
   *
   * `window` rather than `document`, because the dialog captures Escape on
   * `document`, and `window` comes earlier in the capture path.
   */
  useEffect(() => {
    if (!picking && !searchOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      if (searchOpen) setSearchOpen(false);
      else onPickingChange?.(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [picking, searchOpen, onPickingChange]);

  if (!value && !picking) return null;

  return (
    <div className={`place-map ${expanded ? 'expanded' : ''}`} ref={boxRef}>
      <MapCanvas
        markers={markers}
        fitBounds={fitBounds}
        fitKey={fitKey}
        picking={picking}
        onMapClick={handleClick}
        onInteract={expand}
        tiles={tiles}
        focusOn={focusOn}
        zoom={6}
      />

      <MapSearch
        open={searchOpen}
        setOpen={setSearchOpen}
        onGoTo={(place) => {
          expand();
          onPickingChange?.(true);
          setSearched(place);
          setFocusOn({ lat: place.lat, lng: place.lng, bbox: place.bbox, nonce: Date.now() });
        }}
      />
      {picking ? (
        <div className="place-map-hint">
          <span style={{ flex: 1 }}>
            {value
              ? 'Touchez ailleurs pour deplacer le point.'
              : 'Touchez la carte pour placer ce point.'}
          </span>
          {value && (
            <button type="button" className="btn btn-sm btn-primary" onClick={() => onPickingChange?.(false)}>
              Terminer
            </button>
          )}
        </div>
      ) : (
        expanded && (
          <button type="button" className="btn btn-sm place-map-shrink" onClick={() => setExpanded(false)}>
            Reduire
          </button>
        )
      )}
    </div>
  );
}
